import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { batchApi } from "../../api/batchApi";
import { useApiQuery } from "../../hooks/useApiQuery";
import { useApiMutation } from "../../hooks/useApiMutation";
import { useToast } from "../../components/ui/toast/ToastProvider";
import { downloadBlob } from "../../services/fileDownload";
import { toDispatchHistoryResponse } from "../batches/batchAdapters";
import BatchListControls from "../batches/components/BatchListControls";
import DispatchHistoryToolbar from "./components/DispatchHistoryToolbar";
import DispatchHistoryTable from "./components/DispatchHistoryTable";

const ROWS_PER_PAGE = 10;

/**
 * Dispatch History — a dedicated page/route (not a Batch sub-tab), driven
 * by GET /tracesync/batch/dispatch-history through the same `batchApi`
 * module and adapter layer the Batch list uses, and built from the same
 * shared UI (DataTable, BatchListControls, BatchRowActions) so it stays
 * visually identical to the Batch table per the brief.
 */
const DispatchHistory = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const listParams = useMemo(
    () => ({
      page,
      limit: ROWS_PER_PAGE,
      search: searchTerm || undefined,
      dispatch_from: dateFrom || undefined,
      dispatch_to: dateTo || undefined,
    }),
    [page, searchTerm, dateFrom, dateTo]
  );

  const {
    data: listResult,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useApiQuery(
    () => batchApi.getDispatchHistory(listParams).then(toDispatchHistoryResponse),
    [listParams.page, listParams.search, listParams.dispatch_from, listParams.dispatch_to]
  );

  const { run: downloadSlip, isLoading: isDownloadingSlip } = useApiMutation(batchApi.downloadDispatchSlip);

  const rows = listResult?.rows || [];
  const totalRecords = listResult?.total ?? rows.length;

  const handleDownload = async (row) => {
    if (!row.uid) return;
    try {
      const blob = await downloadSlip(row.uid);
      downloadBlob(blob, `dispatch-slip-${row.batchId}.pdf`);
      toast.success("Dispatch slip downloaded.");
    } catch (err) {
      toast.error(err?.message || "Unable to download dispatch slip.");
    }
  };

  const handleRowAction = (action, row) => {
    if (action === "view") return navigate(`/setting/batch/${encodeURIComponent(row.uid)}`);
    if (action === "download") return handleDownload(row);
  };

  const hasFilters = Boolean(searchTerm || dateFrom || dateTo);

  return (
    <div className="px-3 sm:px-5">
      <DispatchHistoryToolbar
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={(value) => {
          setDateFrom(value);
          setPage(1);
        }}
        onDateToChange={(value) => {
          setDateTo(value);
          setPage(1);
        }}
        onClearDates={() => {
          setDateFrom("");
          setDateTo("");
          setPage(1);
        }}
        onRefresh={refetch}
        isRefreshing={isFetching}
      />

      <BatchListControls
        page={page}
        totalRecords={totalRecords}
        rowsPerPage={ROWS_PER_PAGE}
        onPageChange={setPage}
        searchTerm={searchTerm}
        onSearchChange={(value) => {
          setSearchTerm(value);
          setPage(1);
        }}
        searchPlaceholder="Search batch, dispatched to..."
      />

      {error ? (
        <div className="rounded-lg border border-rose-100 bg-rose-50 p-6 text-center text-sm text-rose-600">
          {error.message || "Unable to load dispatch history."}
          <button type="button" onClick={refetch} className="ml-2 font-semibold underline">
            Retry
          </button>
        </div>
      ) : (
        <DispatchHistoryTable
          rows={rows.map((row) => ({ ...row, canDownloadSlip: Boolean(row.uid) && !isDownloadingSlip }))}
          isLoading={isLoading}
          noDataLabel={hasFilters ? "No matching dispatch records found" : "No dispatch history found"}
          onRowAction={handleRowAction}
        />
      )}
    </div>
  );
};

export default DispatchHistory;
